/**
 * Emergency Contacts Section Component
 * Displays emergency contact information
 * Follows Single Responsibility Principle
 */

import React from 'react';
import { motion } from 'framer-motion';
import { spacing } from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';
import { EmergencyContactCard } from './EmergencyContactCard';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export interface EmergencyContactItem {
  title: string;
  value: string | null;
  icon?: string | React.ReactNode;
}

export interface EmergencyContactsSectionProps {
  contacts: EmergencyContactItem[];
  className?: string;
}

export const EmergencyContactsSection: React.FC<
  EmergencyContactsSectionProps
> = ({ contacts, className }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (!contacts || contacts.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: 0.25,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.4 },
      }}
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
    >
      <SectionHeader title='Emergency Contacts' />
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row', // Column on mobile, row on desktop
          gap: spacing[2],
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
          width: '100%',
        }}
      >
        {contacts.map((contact, index) => (
          <EmergencyContactCard
            key={index}
            title={contact.title}
            value={contact.value}
            icon={contact.icon}
            index={index}
          />
        ))}
      </div>
    </motion.div>
  );
};
